import Vue from "vue";
import urls from "../../constants/urls.js";
import axios from "axios";

const state = {
  integrations: [],
  mailChimpIntegrations: [],
  loadingIntegrations: true,
  loadingMailChimpList: false,
  // integration id -> proc_session_id of a manual sync believed to be in flight,
  // so reopening the sync modal reattaches instead of starting a second run.
  activeSyncRuns: {},
};

const mutations = {
  setIntegrations(state, data) {
    state.integrations = data;
  },

  setLoadingIntegrations(state, value) {
    state.loadingIntegrations = value;
  },

  setLoadingMailChimpList(state, value) {
    state.loadingMailChimpList = value;
  },

  setMailchimpIntegrations(state, data) {
    state.mailChimpIntegrations = data;
  },

  setActiveSyncRun(state, { integrationId, procSessionId }) {
    Vue.set(state.activeSyncRuns, String(integrationId), procSessionId);
  },

  clearActiveSyncRun(state, integrationId) {
    Vue.delete(state.activeSyncRuns, String(integrationId));
  },
};

const getters = {
  getintegratuimByType: (state) => (type) => {
    return state.integrations.filter(function (i) {
      return i.attributes.service === type;
    });
  },
};

const actions = {
  getIntegrationsList({ commit }) {
    let orgId = localStorage.getItem("orgId");
    commit("setLoadingIntegrations", true);
    return axios
      .get(urls.getIntegrationsList.replace(":orgId", orgId))
      .then((response) => {
        let integrations = response.data.data;
        commit("setIntegrations", integrations);
      })
      .catch((error) => {
        // Without this the loading flag stays true and the page spins forever.
        console.error(error);
      })
      .finally(() => {
        commit("setLoadingIntegrations", false);
      });
  },

  getMailChimpIntegrationList({ commit }, id) {
    let orgId = localStorage.getItem("orgId");
    commit("setLoadingMailChimpList", true);
    axios
      .get(urls.getMailChimpIntegrationList.replace(":orgId", orgId).replace(":id", id))
      .then((response) => {
        let list = response.data.data;
        commit("setMailchimpIntegrations", list);
        commit("setLoadingMailChimpList", false);
      });
  },

  // Starts a manual sync. A 409 `sync_already_running` is not an error here:
  // the guard is per organization, so two coordinators pressing "Sync now"
  // should converge on the run already in flight rather than see a failure.
  startIntegrationSync({ commit }, { integrationId, campaignId } = {}) {
    const orgId = localStorage.getItem("orgId");
    const payload = campaignId ? { campaign_id: Number(campaignId) } : {};

    return axios
      .post(urls.integrationSync(orgId, integrationId), payload)
      .then(({ data }) => {
        const run = data.data;
        commit("setActiveSyncRun", { integrationId, procSessionId: run.proc_session_id });
        return { attached: false, run };
      })
      .catch((error) => {
        const body = error?.response?.data || {};
        const conflict = (body.errors || []).find((e) => e?.code === "sync_already_running");
        const runningId = conflict?.meta?.proc_session_id || body.meta?.proc_session_id;

        if (conflict && runningId) {
          commit("setActiveSyncRun", { integrationId, procSessionId: runningId });
          return { attached: true, run: { proc_session_id: runningId, campaign_ids: [] } };
        }

        throw error;
      });
  },

  fetchIntegrationSyncStatus(_, { integrationId, procSessionId }) {
    const orgId = localStorage.getItem("orgId");

    return axios
      .get(urls.integrationSyncStatus(orgId, integrationId, procSessionId))
      .then(({ data }) => data.data);
  },

  fetchIntegrationLogs(_, { service, campaignId, page = 1, perPage = 25 } = {}) {
    const orgId = localStorage.getItem("orgId");
    const params = { service, page, per_page: Math.min(perPage, 100) };

    if (campaignId) {
      params.campaign_id = Number(campaignId);
    }

    return axios.get(urls.integrationLogs(orgId), { params }).then(({ data }) => ({
      list: data.data || [],
      total: data.meta?.total || 0,
    }));
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
